import assert from 'assert';

import {IconMapping} from 'schemas/schema-interface/v2/getSchemaInterfaceIcon';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';

import {ComplexPolymerInterfaceQuery_complexPolymerSchemaConfiguration_GenericComplexPolymerSchemaConfigNode_OligoConjugateSchemaConfigNode_OligoDuplexSchemaConfigNode as ComplexPolymerSchemaInterfaceGQL} from './__generated/ComplexPolymerInterfaceQuery';
import {ComplexPolymerType} from './ComplexPolymerConstants';

// Keep in sync with `ComplexPolymerInterfaceApiId` in `benchling/modbio/complex_polymers/core/complex_polymer_schema_configuration.py`
// TODO(BNCH-122853): This is not currently in sync with the BE version because we have not gotten around to removing Antibody from this enum on the backend
export enum ComplexPolymerInterfaceApiId {
  OLIGO_CONJUGATE_ID = 'ssi_oligoconjugate',
  OLIGO_DUPLEX_ID = 'ssi_oligoduplex',
}

const COMPLEX_POLYMER_INTERFACE_API_ID_TO_TYPE: {
  [key in ComplexPolymerInterfaceApiId]: ComplexPolymerType;
} = {
  [ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID]: ComplexPolymerType.OLIGO_CONJUGATE,
  [ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID]: ComplexPolymerType.OLIGO_DUPLEX,
};

const COMPLEX_POLYMER_TYPE_TO_INTERFACE_API_ID: {
  [key in ComplexPolymerType]: ComplexPolymerInterfaceApiId;
} = {
  [ComplexPolymerType.OLIGO_CONJUGATE]: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
  [ComplexPolymerType.OLIGO_DUPLEX]: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
};

export interface ComplexPolymerSchemaFieldSpec {
  schemaFieldName: string;
  schemaOrInterfaceId: string;
}

// Keep in sync with OLIGO_CONJUGATE_SHAPE in benchling/modbio/complex_polymers/api/rest/schema_config_params.py
export interface ImplementedOligoConjugateInterface extends ImplementedStaticSchemaInterface {
  interfaceId: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID;
  oligoSchemaFieldSpec: ComplexPolymerSchemaFieldSpec;
  conjugatesSchemaFieldSpecs: Array<ComplexPolymerSchemaFieldSpec>;
}

// Keep in sync with OLIGO_DUPLEX_SHAPE in benchling/modbio/complex_polymers/api/rest/schema_config_params.py
export interface ImplementedOligoDuplexInterface extends ImplementedStaticSchemaInterface {
  interfaceId: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID;
  forwardStrandSchemaFieldSpec: ComplexPolymerSchemaFieldSpec;
  reverseStrandSchemaFieldSpec: ComplexPolymerSchemaFieldSpec;
  conjugatesSchemaFieldSpecs: Array<ComplexPolymerSchemaFieldSpec>;
}

export type DraftImplementedOligoConjugateInterface = Pick<
  ImplementedOligoConjugateInterface,
  'interfaceId'
> &
  Partial<ImplementedOligoConjugateInterface>;

export type DraftImplementedOligoDuplexInterface = Pick<ImplementedOligoDuplexInterface, 'interfaceId'> &
  Partial<ImplementedOligoDuplexInterface>;

export type DraftImplementedComplexPolymerInterface =
  | DraftImplementedOligoConjugateInterface
  | DraftImplementedOligoDuplexInterface;

export type ImplementedComplexPolymerInterface =
  | ImplementedOligoConjugateInterface
  | ImplementedOligoDuplexInterface;

export type ComplexPolymerInterfaceFieldName =
  | keyof Omit<ImplementedOligoConjugateInterface, 'interfaceId'>
  | keyof Omit<ImplementedOligoDuplexInterface, 'interfaceId'>;

export const CLIENT_COMPLEX_POLYMER_INTERFACE_API_IDS: Array<string> = Object.values(
  ComplexPolymerInterfaceApiId
).map((v) => v.toString());

export function isComplexPolymerInterfaceId(interfaceId: string): boolean {
  return Object.values(ComplexPolymerInterfaceApiId)
    .map((v) => v.toString())
    .includes(interfaceId);
}

export function isComplexPolymerImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedComplexPolymerInterface | DraftImplementedComplexPolymerInterface {
  return isComplexPolymerInterfaceId(implementedInterface.interfaceId);
}

export function isOligoConjugateImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedOligoConjugateInterface | DraftImplementedOligoConjugateInterface {
  return implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID;
}

export function isOligoDuplexImplementedInterface(
  implementedInterface: ImplementedStaticSchemaInterface
): implementedInterface is ImplementedOligoDuplexInterface | DraftImplementedOligoDuplexInterface {
  return implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID;
}

export function implementedInterfaceToComplexPolymerType(
  implementedInterface?: ImplementedStaticSchemaInterface | null
): ComplexPolymerType | null {
  if (implementedInterface == null || !isComplexPolymerImplementedInterface(implementedInterface)) {
    return null;
  }
  return COMPLEX_POLYMER_INTERFACE_API_ID_TO_TYPE[implementedInterface.interfaceId];
}

export function complexPolymerTypeToSchemaInterfaceId(
  complexPolymerType: ComplexPolymerType
): ComplexPolymerInterfaceApiId {
  return COMPLEX_POLYMER_TYPE_TO_INTERFACE_API_ID[complexPolymerType];
}

export function isCompleteImplementedComplexPolymerInterface(
  implementedInterface: DraftImplementedComplexPolymerInterface
): implementedInterface is ImplementedComplexPolymerInterface {
  if (implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID) {
    return (
      implementedInterface.oligoSchemaFieldSpec != null &&
      implementedInterface.conjugatesSchemaFieldSpecs != null &&
      implementedInterface.conjugatesSchemaFieldSpecs.length > 0
    );
  }
  if (implementedInterface.interfaceId === ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID) {
    return (
      implementedInterface.forwardStrandSchemaFieldSpec != null &&
      implementedInterface.reverseStrandSchemaFieldSpec != null &&
      implementedInterface.conjugatesSchemaFieldSpecs != null
    );
  }

  return false;
}

const _SCHEMA_INTERFACE_GQL_TYPENAME_TO_COMPLEX_POLYMER_TYPE: {
  [k in ComplexPolymerSchemaInterfaceGQL['__typename']]: ComplexPolymerType | null;
} = {
  // TODO(BNCH-119541): Remove this. For now, these don't exist in the wild so we can just return null.
  GenericComplexPolymerSchemaConfigNode: null,
  OligoConjugateSchemaConfigNode: ComplexPolymerType.OLIGO_CONJUGATE,
  OligoDuplexSchemaConfigNode: ComplexPolymerType.OLIGO_DUPLEX,
};

export function graphqlSchemaInterfaceToComplexPolymerType(schemaInterface: {
  __typename: ComplexPolymerSchemaInterfaceGQL['__typename'];
}): ComplexPolymerType {
  const {__typename} = schemaInterface;
  const type = _SCHEMA_INTERFACE_GQL_TYPENAME_TO_COMPLEX_POLYMER_TYPE[__typename];
  assert(type != null, 'Expected to find a matching complex polymer type.');
  return type;
}

export const COMPLEX_POLYMER_SCHEMA_INTERFACE_ICON_MAPPING: IconMapping = {
  [ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID]: 'network-wired',
  [ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID]: 'network-wired',
};
